// Password-protected PDFs (e.g. Indian Aadhaar e-PDFs) cannot be malware-scanned, so the backend
// blocks them for good and nobody can open them. Pickers reject them up front with this check.
//
// Only PDFs that need a password to OPEN are rejected. PDFs that are merely restricted (no
// printing/copying, no open password) still open normally and are allowed.
// pdf.js is loaded on demand, only when a PDF is picked. The legacy build supports older iPhones.

export const PASSWORD_PROTECTED_PDF_MESSAGE =
    'This PDF is password-protected. Please upload a version without a password.'

const isPdf = (file) =>
    file?.type === 'application/pdf' || /\.pdf$/i.test(file?.name || '')

let pdfjsPromise

const loadPdfjs = () => {
    if (!pdfjsPromise) {
        pdfjsPromise = import('pdfjs-dist/legacy/build/pdf.mjs').then((pdfjs) => {
            pdfjs.GlobalWorkerOptions.workerSrc = new URL(
                'pdfjs-dist/legacy/build/pdf.worker.min.mjs',
                import.meta.url,
            ).toString()
            return pdfjs
        })
        // Allow a retry on the next pick if loading failed (e.g. a network blip).
        pdfjsPromise.catch(() => { pdfjsPromise = undefined })
    }
    return pdfjsPromise
}

// True only when pdf.js positively reports that the PDF needs a password to open. Any other failure
// (not a PDF, corrupt file, pdf.js failed to load) returns false. The backend checks still apply.
export const isPasswordProtectedPdf = async (file) => {
    if (!file || !isPdf(file)) return false

    let loadingTask
    try {
        const pdfjs = await loadPdfjs()
        const data = new Uint8Array(await file.arrayBuffer())
        loadingTask = pdfjs.getDocument({
            data,
            // We only need to know whether it opens, so skip fonts, eval and extra fetches.
            disableFontFace: true,
            isEvalSupported: false,
            stopAtErrors: false,
        })
        // pdf.js asks for a password through onPassword. Cancel here rather than wait for one.
        let needsPassword = false
        loadingTask.onPassword = () => {
            needsPassword = true
            loadingTask.destroy()
        }
        await loadingTask.promise.catch((error) => {
            if (error?.name === 'PasswordException') needsPassword = true
        })
        return needsPassword
    }
    catch {
        return false
    }
    finally {
        loadingTask?.destroy?.()
    }
}

// Returns the files that are password-protected PDFs (checked in parallel).
export const findPasswordProtectedPdfs = async (files = []) => {
    const list = Array.from(files)
    const flags = await Promise.all(list.map(isPasswordProtectedPdf))
    return list.filter((_, index) => flags[index])
}
